// Shared geometry helpers for shape calculations

import type { Point } from '@/modules/Node';

interface Line {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface LineSeg {
  x3: number;
  y3: number;
  x4: number;
  y4: number;
}

function getLinesIntersectionCoords(line1: Line, line2: LineSeg): Point | null {
  const { x1, y1, x2, y2 } = line1;
  const { x3, y3, x4, y4 } = line2;

  const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
  if (denom === 0) return null;

  const uA = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
  const uB = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

  if (uA >= 0 && uA <= 1 && uB >= 0 && uB <= 1) {
    return {
      x: x1 + uA * (x2 - x1),
      y: y1 + uA * (y2 - y1),
    };
  }
  return null;
}

function getLineLength(x1: number, x2: number, y1: number, y2: number): number {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

function findEdgeSideIntersection(
  edgeLine: Line,
  sides: LineSeg[]
): Point {
  for (const side of sides) {
    const intersection = getLinesIntersectionCoords(edgeLine, side);
    if (intersection) return intersection;
  }
  return { x: 0, y: 0 };
}

export { getLinesIntersectionCoords, getLineLength, findEdgeSideIntersection };
export type { Line, LineSeg };
